import { type EvmCurrency, type EvmNative, EvmToken } from 'sushi/evm'
import type * as z from 'zod'
import type {
  tokenValidator as tokenValidator01,
  tradeValidator01,
} from './validator01'
import type { trailsQuoteValidator } from './validator02'

type token1 = z.infer<typeof tokenValidator01>
export type swapApi1 = z.infer<typeof tradeValidator01>
export type TrailsQuoteResponse = z.infer<typeof trailsQuoteValidator>

function getApi1Token(token: EvmToken | EvmNative): token1 {
  if (token instanceof EvmToken)
    return {
      chainId: token.chainId,
      decimals: token.decimals,
      symbol: token.symbol as string,
      name: token.name as string,
      isNative: false,
      isToken: true,
      address: token.address,
      tokenId: token.id,
    }
  else
    return {
      chainId: token.chainId,
      decimals: token.decimals,
      symbol: token.symbol,
      name: token.name,
      isNative: true,
      isToken: false,
      tokenId: token.id,
    }
}

export function trailsAdapterToInternal(
  res: TrailsQuoteResponse,
  fromToken: EvmCurrency,
  toToken: EvmCurrency,
  to: string | undefined,
): swapApi1 & {
  intentId: string
  rawIntent: any
  feeUsd: number | undefined
  feePercent: number | undefined
} {
  const { intent } = res
  const { quote, depositTransaction, fees } = intent

  const amountInBI = BigInt(quote.fromAmount)
  const amountOutBI = BigInt(quote.toAmount)

  // Use API-provided price impact (percentage, e.g. 0.15 = 0.15%)
  const priceImpact = quote.priceImpact ?? 0

  // Swap fee = provider fee + trails fee, taken straight from the quote.
  const providerFeeUsd = fees?.providerFeeUsd
  const trailsFeeUsd = fees?.trailsFeeUsd
  const feeUsd =
    providerFeeUsd !== undefined || trailsFeeUsd !== undefined
      ? (providerFeeUsd ?? 0) + (trailsFeeUsd ?? 0)
      : undefined

  const providerFeePct =
    quote.priceImpactDetails?.providerFeesPriceImpact?.priceImpact
  const trailsFeePct =
    quote.priceImpactDetails?.trailsFeesPriceImpact?.priceImpact
  const feePercent =
    providerFeePct !== undefined || trailsFeePct !== undefined
      ? (providerFeePct ?? 0) + (trailsFeePct ?? 0)
      : undefined

  // Compute swap price as a ratio
  const fromDecimals = fromToken.decimals
  const toDecimals = toToken.decimals
  const swapPrice =
    Number(quote.toAmount) /
    10 ** toDecimals /
    (Number(quote.fromAmount) / 10 ** fromDecimals)

  const route = {
    status: 'Success' as const,
    fromToken: getApi1Token(fromToken),
    toToken: getApi1Token(toToken),
    swapPrice,
    priceImpact,
    amountIn: Number(quote.fromAmount),
    amountInBI,
    amountOut: Number(quote.toAmount),
    amountOutBI,
    gasSpent: 0,
    totalAmountOut: Number(quote.toAmount),
    totalAmountOutBI: amountOutBI,
  }

  // Use the deposit tx fields from the API response directly
  const depositTo =
    (depositTransaction.to as `0x${string}`) ??
    (depositTransaction.toAddress as `0x${string}`)
  const depositData = (depositTransaction.data as `0x${string}`) ?? '0x'
  const depositValue = depositTransaction.value
    ? BigInt(depositTransaction.value)
    : undefined

  return {
    route,
    args: to
      ? {
          amountIn: amountInBI,
          amountOutMin: quote.toAmountMin
            ? BigInt(quote.toAmountMin)
            : amountOutBI,
          to,
          tokenIn:
            fromToken.type === 'native'
              ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
              : fromToken.wrap().address,
          tokenOut:
            toToken.type === 'native'
              ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
              : toToken.wrap().address,
          routeCode: '',
        }
      : undefined,
    tx: {
      from: '0x0000000000000000000000000000000000000000' as `0x${string}`,
      to: depositTo,
      data: depositData,
      value: depositValue,
    },
    intentId: intent.intentId,
    rawIntent: res.intent,
    feeUsd,
    feePercent,
  }
}

export function trailsNoRouteResponse(
  fromToken: EvmCurrency,
  toToken: EvmCurrency,
  to: string | undefined,
): swapApi1 {
  const route = {
    status: 'NoWay' as const,
    fromToken: getApi1Token(fromToken),
    toToken: getApi1Token(toToken),
    amountIn: 0,
    amountInBI: 0n,
    amountOut: 0,
    amountOutBI: 0n,
    gasSpent: 0,
    totalAmountOut: 0,
    totalAmountOutBI: 0n,
  }

  if (to !== undefined) {
    return {
      route,
      args: {
        amountIn: 0n,
        amountOutMin: 0n,
        to,
        tokenIn:
          fromToken.type === 'native'
            ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
            : fromToken.wrap().address,
        tokenOut:
          toToken.type === 'native'
            ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
            : toToken.wrap().address,
        routeCode: '',
      },
    }
  }

  return { route }
}
